import { Link } from "react-router-dom";
import { Phone, Mail, Globe, ShieldCheck, FileDown, ArrowRight, Landmark, Lock } from "lucide-react";

const CARRIERS = [
  "American Amicable", "Americo", "F&G", "Foresters Financial", "Banner Life",
  "SBLI", "United Home Life", "Mutual of Omaha", "Transamerica",
  "Corebridge Financial", "Assurity",
];

const Header = () => (
  <header className="sticky top-0 z-40 backdrop-blur-md bg-[#FDFBF7]/90 border-b border-[#D4AF37]/25">
    <div className="max-w-6xl mx-auto px-4 py-3 flex items-center justify-between">
      <Link to="/" className="flex items-center gap-3" data-testid="header-brand-link">
        <div className="w-10 h-10 rounded-full border border-[#D4AF37]/60 bg-[#0B2319] flex items-center justify-center">
          <span className="font-serif font-bold text-[#D4AF37] text-sm tracking-widest">SC</span>
        </div>
        <div>
          <p className="font-serif font-semibold text-[#0B2319] leading-tight text-sm sm:text-base">
            Simonne &amp; Co. Financial Solutions
          </p>
          <p className="text-[10px] sm:text-xs text-[#B8860B] tracking-[0.2em] uppercase">
            Protect. Plan. Build. Live.
          </p>
        </div>
      </Link>
      <div className="flex items-center gap-3">
        <a href="tel:2056003708" data-testid="header-phone-link"
           className="hidden sm:flex items-center gap-1.5 text-sm text-[#0B2319] hover:text-[#B8860B] transition-colors">
          <Phone size={14} className="text-[#C5A059]" /> 205-600-3708
        </a>
        <Link to="/admin/login" data-testid="header-admin-link"
              className="flex items-center gap-1 text-xs text-[#5A6B63] hover:text-[#0B2319] transition-colors">
          <Lock size={12} /> Agent
        </Link>
      </div>
    </div>
  </header>
);

export const Footer = () => (
  <footer className="bg-[#081A13] text-[#D8CDB8]">
    <div className="max-w-6xl mx-auto px-4 py-12">
      <div className="grid md:grid-cols-2 gap-8">
        <div>
          <p className="font-serif text-2xl text-[#FDFBF7]">Simonne &amp; Co. Financial Solutions</p>
          <p className="font-serif italic text-[#C5A059] mt-1">Protect. Plan. Build. Live.</p>
          <p className="text-sm mt-3 text-[#9DB0A6]">Life Insurance Solutions for Real Life.</p>
        </div>
        <div className="space-y-2 text-sm">
          <a href="tel:2056003708" data-testid="footer-phone-link" className="flex items-center gap-2 hover:text-[#FCF6BA] transition-colors">
            <Phone size={14} className="text-[#C5A059]" /> 205-600-3708
          </a>
          <a href="mailto:simonnecofinancial@gmail.com" data-testid="footer-email-link" className="flex items-center gap-2 hover:text-[#FCF6BA] transition-colors">
            <Mail size={14} className="text-[#C5A059]" /> simonnecofinancial@gmail.com
          </a>
          <a href="https://simonnecofinancial.com" target="_blank" rel="noopener noreferrer" data-testid="footer-website-link" className="flex items-center gap-2 hover:text-[#FCF6BA] transition-colors">
            <Globe size={14} className="text-[#C5A059]" /> simonnecofinancial.com
          </a>
        </div>
      </div>
      <div className="gold-rule my-8 opacity-60" />
      <div className="text-[11px] leading-relaxed text-[#7E9184] space-y-1.5" data-testid="footer-compliance">
        <p>Quotes are estimates only. Final rates, eligibility, product availability and coverage are subject to carrier underwriting and policy approval.</p>
        <p>Policy features, riders and living benefits vary by carrier, product and state.</p>
        <p>Life insurance policy loans and withdrawals may reduce cash value and death benefit and may have tax consequences.</p>
        <p>Indexed Universal Life policies are not direct investments in a stock market index.</p>
      </div>
    </div>
  </footer>
);

export default function Landing() {
  return (
    <div className="min-h-screen bg-[#FDFBF7]" data-testid="landing-page">
      <Header />

      {/* Hero */}
      <section className="max-w-6xl mx-auto px-4 pt-10 pb-14 sm:pt-16">
        <div className="grid md:grid-cols-2 gap-10 items-center">
          <div className="fade-up">
            <p className="text-xs sm:text-sm tracking-[0.25em] uppercase text-[#B8860B] font-semibold">
              Life Insurance Solutions for Real Life.
            </p>
            <h1 className="font-serif text-4xl sm:text-5xl lg:text-6xl font-bold text-[#0B2319] mt-4 leading-[1.05]">
              Protect What<br />Matters Most.
            </h1>
            <p className="text-base sm:text-lg text-[#5A6B63] mt-5 leading-relaxed max-w-xl">
              Life insurance is about more than funeral expenses. The right policy can help protect
              income, housing, debt, children, business obligations, future plans, and your family's
              financial stability.
            </p>
            <div className="mt-8 space-y-4">
              <Link to="/quote" data-testid="start-quote-button"
                    className="w-full sm:w-auto inline-flex items-center justify-center gap-2 h-14 px-8 rounded-xl bg-[#0B2319] hover:bg-[#081A13] text-[#FCF6BA] text-base font-semibold tracking-wide border border-[#D4AF37]/40 transition-colors">
                START MY FREE QUOTE <ArrowRight size={18} />
              </Link>
              <div className="card-gold p-5 max-w-xl" data-testid="guide-download-card">
                <p className="font-serif text-lg font-semibold text-[#0B2319]">
                  Not sure what type of coverage you need?
                </p>
                <p className="text-sm text-[#5A6B63] mt-1.5 leading-relaxed">
                  Download my detailed Life Insurance Policy Guide to compare your options and learn
                  which type of protection may fit your needs.
                </p>
                <a href="/assets/policy-guide.pdf" download data-testid="download-guide-button"
                   className="mt-4 inline-flex items-center justify-center gap-2 h-12 px-6 rounded-xl border border-[#D4AF37] text-[#0B2319] font-semibold text-sm hover:bg-[#FAF4E8] transition-colors w-full sm:w-auto">
                  <FileDown size={16} className="text-[#B8860B]" /> DOWNLOAD POLICY GUIDE
                </a>
              </div>
            </div>
          </div>

          <div className="fade-up" style={{ animationDelay: "0.15s" }}>
            <div className="relative max-w-sm mx-auto">
              <div className="absolute -inset-3 rounded-t-[999px] rounded-b-3xl border border-[#D4AF37]/50" />
              <div className="relative rounded-t-[999px] rounded-b-3xl overflow-hidden border border-[#D4AF37]/40 shadow-2xl">
                <img src="/assets/advisor.jpg" alt="Simonne - Simonne & Co. Financial Solutions"
                     data-testid="advisor-photo" className="w-full h-full object-cover" />
              </div>
              <div className="absolute -bottom-5 left-1/2 -translate-x-1/2 card-gold px-5 py-3 flex items-center gap-2.5 whitespace-nowrap">
                <ShieldCheck size={18} className="text-[#B8860B]" />
                <span className="text-xs font-semibold text-[#0B2319]">Independent Agent &middot; Multiple Carriers</span>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Tagline strip */}
      <section className="bg-[#0B2319] py-8">
        <p className="text-center font-serif italic text-xl sm:text-2xl tracking-wide" data-testid="tagline-strip">
          <span className="gold-text-gradient">Protect. Plan. Build. Live.</span>
        </p>
      </section>

      {/* Carriers */}
      <section className="max-w-6xl mx-auto px-4 py-14 sm:py-20" data-testid="carriers-section">
        <div className="text-center max-w-2xl mx-auto">
          <p className="text-xs tracking-[0.25em] uppercase text-[#B8860B] font-semibold">Carrier Access</p>
          <h2 className="font-serif text-3xl sm:text-4xl font-bold text-[#0B2319] mt-3">
            Access to Multiple Carriers
          </h2>
          <p className="text-base text-[#5A6B63] mt-4 leading-relaxed">
            &ldquo;Every family is different. Having access to multiple carriers helps me compare
            products, underwriting and pricing to find options that may fit your needs.&rdquo;
          </p>
        </div>
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3 mt-10">
          {CARRIERS.map((c) => (
            <div key={c} data-testid={`carrier-chip-${c.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
                 className="card-gold px-4 py-5 flex flex-col items-center gap-2 text-center hover:shadow-lg transition-shadow">
              <Landmark size={18} className="text-[#C5A059]" />
              <span className="font-serif font-semibold text-[#0B2319] text-sm leading-snug">{c}</span>
            </div>
          ))}
        </div>
        <p className="text-center text-xs text-[#5A6B63] mt-8 max-w-2xl mx-auto leading-relaxed" data-testid="carrier-disclaimer">
          Carrier and product availability varies by state, age, health, underwriting and appointment
          status. Not every carrier offers every policy type.
        </p>
      </section>

      {/* CTA band */}
      <section className="max-w-6xl mx-auto px-4 pb-16">
        <div className="rounded-3xl bg-[#0B2319] border border-[#D4AF37]/30 px-6 py-12 text-center">
          <h2 className="font-serif text-3xl sm:text-4xl font-bold text-[#FDFBF7]">
            Plan Today. <span className="italic gold-text-gradient">Secure Tomorrow.</span>
          </h2>
          <p className="text-[#9DB0A6] mt-3 max-w-xl mx-auto text-sm sm:text-base">
            Answer a few quick questions and I'll compare options across carriers for you and your family.
          </p>
          <Link to="/quote" data-testid="cta-start-quote-button"
                className="mt-7 inline-flex items-center justify-center gap-2 h-14 px-10 rounded-xl bg-[#C5A059] hover:bg-[#B8860B] text-[#0B2319] text-base font-bold tracking-wide transition-colors">
            START MY FREE QUOTE <ArrowRight size={18} />
          </Link>
        </div>
      </section>

      <Footer />
    </div>
  );
}
